import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, X, AlertTriangle } from 'lucide-react';
import { isMobileDevice, getDeviceType } from '../utils/deviceDetection';

export default function MobileWarningBanner() {
  const [showWarning, setShowWarning] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user dismissed the warning in this session
    const wasDismissed = sessionStorage.getItem('mobile-warning-dismissed');
    
    if (!wasDismissed && isMobileDevice()) {
      setShowWarning(true);
      setDeviceType(getDeviceType());
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    setShowWarning(false);
    sessionStorage.setItem('mobile-warning-dismissed', 'true');
  };

  const handleContinueAnyway = () => {
    handleDismiss();
  };

  if (!showWarning || dismissed) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-yellow-500 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Warning Icon Header */}
        <div className="bg-gradient-to-r from-yellow-600 to-orange-600 p-6 text-center">
          <div className="flex justify-center mb-3">
            <AlertTriangle className="w-16 h-16 text-white animate-pulse" />
          </div>
          <h2 className="text-2xl font-bold text-white uppercase tracking-wide">
            ⚠️ Device Warning
          </h2>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="text-center">
            <p className="text-gray-300 text-lg mb-2">
              You're on a <strong className="text-yellow-400">{deviceType}</strong>
            </p>
            <p className="text-gray-400 text-sm">
              Global Thermo Nuclear War ]|[ is optimized for desktop browsers
            </p>
          </div>

          {/* Device comparison */}
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Mobile (Not Recommended) */}
            <div className="bg-red-900/20 border border-red-700 rounded-lg p-3 text-center">
              <Smartphone className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <div className="text-xs text-red-400 font-bold mb-1">MOBILE</div>
              <div className="text-[10px] text-gray-400">
                ❌ Cramped UI<br/>
                ❌ Hard to tap<br/>
                ❌ Limited view
              </div>
            </div>

            {/* Desktop (Recommended) */}
            <div className="bg-green-900/20 border border-green-700 rounded-lg p-3 text-center">
              <Monitor className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <div className="text-xs text-green-400 font-bold mb-1">DESKTOP</div>
              <div className="text-[10px] text-gray-400">
                ✅ Full UI<br/>
                ✅ Easy controls<br/>
                ✅ Best experience
              </div>
            </div>
          </div>

          {/* Recommendation */}
          <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Monitor className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-300">
                <strong className="text-blue-400">Recommended:</strong> Switch to a desktop or laptop computer for the best tactical gaming experience.
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-2 pt-2">
            <button
              onClick={handleContinueAnyway}
              className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Continue Anyway
            </button>
            <button
              onClick={handleDismiss}
              className="w-full bg-slate-700 hover:bg-slate-600 text-gray-300 font-medium py-2 px-6 rounded-lg transition-all duration-200"
            >
              I'll Switch to Desktop
            </button>
          </div>

          {/* Fine print */}
          <p className="text-xs text-gray-500 text-center pt-2">
            This game has complex controls designed for mouse & keyboard
          </p>
        </div>
      </div>
    </div>
  );
}
